import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Image,
  Mic,
  FileText,
  Sparkles,
  Search,
  Copy,
  Star,
  TrendingUp,
  Wand2,
  Palette,
  Camera,
  Briefcase,
  Heart,
  Laugh,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';

interface PromptTemplate {
  id: string;
  title: string;
  prompt: string;
  category: string;
  tags: string[];
  popular?: boolean;
}

const imageTemplates: PromptTemplate[] = [
  {
    id: 'img-1',
    title: 'Professional Portrait',
    prompt: 'Professional headshot portrait photography, soft studio lighting, neutral background, sharp focus, business attire, confident expression, high-quality DSLR photo',
    category: 'Portrait',
    tags: ['business', 'professional', 'portrait'],
    popular: true
  },
  {
    id: 'img-2',
    title: 'Fantasy Landscape',
    prompt: 'Epic fantasy landscape with floating islands, magical aurora in the sky, ancient ruins covered in glowing runes, bioluminescent plants, ethereal atmosphere, ultra detailed digital art',
    category: 'Fantasy',
    tags: ['fantasy', 'landscape', 'magical'],
    popular: true
  },
  {
    id: 'img-3',
    title: 'Product Photography',
    prompt: 'Minimalist product photography, clean white background, professional studio lighting, high-end commercial quality, sharp focus, premium feel',
    category: 'Commercial',
    tags: ['product', 'commercial', 'clean']
  },
  {
    id: 'img-4',
    title: 'Anime Character',
    prompt: 'Beautiful anime character portrait, detailed eyes with sparkles, flowing colorful hair, sakura petals floating, soft pastel colors, Studio Ghibli style, masterpiece quality',
    category: 'Anime',
    tags: ['anime', 'character', 'japanese'],
    popular: true
  },
  {
    id: 'img-5',
    title: 'Cyberpunk City',
    prompt: 'Futuristic cyberpunk cityscape at night, neon lights reflecting on wet streets, holographic advertisements, flying vehicles, dense urban environment, cinematic atmosphere',
    category: 'Sci-Fi',
    tags: ['cyberpunk', 'city', 'futuristic']
  },
  {
    id: 'img-6',
    title: 'Food Photography',
    prompt: 'Mouth-watering food photography, artisanal plating, bokeh background, warm natural lighting, fresh ingredients visible, professional food styling, appetizing colors',
    category: 'Food',
    tags: ['food', 'culinary', 'restaurant']
  },
  {
    id: 'img-7',
    title: 'Nature Close-up',
    prompt: 'Macro photography of dewdrops on flower petals, morning golden hour light, crystal clear water droplets, vibrant natural colors, extreme detail, magical atmosphere',
    category: 'Nature',
    tags: ['nature', 'macro', 'flowers']
  },
  {
    id: 'img-8',
    title: 'Abstract Art',
    prompt: 'Abstract fluid art with swirling metallic gold and deep blue colors, marble effect, luxury aesthetic, organic flowing shapes, high contrast, elegant composition',
    category: 'Abstract',
    tags: ['abstract', 'art', 'fluid']
  }
];

const voiceTemplates: PromptTemplate[] = [
  {
    id: 'voice-1',
    title: 'YouTube Intro',
    prompt: 'Hey everyone! Welcome back to the channel. If you\'re new here, make sure to hit that subscribe button and the notification bell so you never miss an upload. Today, we\'re diving into something really exciting...',
    category: 'YouTube',
    tags: ['youtube', 'intro', 'greeting'],
    popular: true
  },
  {
    id: 'voice-2',
    title: 'Podcast Opening',
    prompt: 'Welcome to another episode of our podcast! I\'m your host, and today we have an incredible topic lined up for you. But before we dive in, I want to thank our amazing listeners for your continued support...',
    category: 'Podcast',
    tags: ['podcast', 'intro', 'host']
  },
  {
    id: 'voice-3',
    title: 'Product Explainer',
    prompt: 'Introducing the revolutionary solution you\'ve been waiting for. Our product combines cutting-edge technology with user-friendly design to transform the way you work. Here\'s how it works...',
    category: 'Marketing',
    tags: ['product', 'explainer', 'business'],
    popular: true
  },
  {
    id: 'voice-4',
    title: 'Meditation Guide',
    prompt: 'Take a deep breath in... and slowly exhale. Allow your body to relax completely. Feel the tension melting away from your shoulders, your neck, your face. You are safe. You are at peace...',
    category: 'Wellness',
    tags: ['meditation', 'calm', 'relaxation']
  },
  {
    id: 'voice-5',
    title: 'News Report',
    prompt: 'Breaking news this evening. We have developing updates on the story that has captivated audiences worldwide. Our correspondent is on the scene with the latest details. Here\'s what we know so far...',
    category: 'News',
    tags: ['news', 'report', 'professional']
  },
  {
    id: 'voice-6',
    title: 'Tutorial Narration',
    prompt: 'In this step-by-step tutorial, I\'ll walk you through everything you need to know. We\'ll start with the basics and work our way up to advanced techniques. Let\'s begin with step one...',
    category: 'Education',
    tags: ['tutorial', 'educational', 'guide'],
    popular: true
  }
];

const scriptTemplates: PromptTemplate[] = [
  {
    id: 'script-1',
    title: 'Viral Hook Formula',
    prompt: 'Create 5 attention-grabbing hooks for a video about [TOPIC]. Each hook should create curiosity, use power words, and make viewers want to watch till the end. Include psychological triggers.',
    category: 'Hooks',
    tags: ['viral', 'hook', 'attention'],
    popular: true
  },
  {
    id: 'script-2',
    title: 'YouTube SEO Titles',
    prompt: 'Generate 10 SEO-optimized YouTube titles for a video about [TOPIC]. Include numbers, power words, and emotional triggers. Make them clickable but not clickbait.',
    category: 'Titles',
    tags: ['youtube', 'seo', 'titles'],
    popular: true
  },
  {
    id: 'script-3',
    title: 'Full Video Script',
    prompt: 'Write a complete 5-minute YouTube script about [TOPIC]. Include: attention-grabbing hook, introduction, 3 main points with examples, call-to-action, and engaging outro.',
    category: 'Scripts',
    tags: ['full', 'script', 'youtube']
  },
  {
    id: 'script-4',
    title: 'Instagram Captions',
    prompt: 'Create 5 engaging Instagram captions for [TOPIC]. Include relevant hashtags, call-to-action, and emojis. Make them conversational and shareable.',
    category: 'Social',
    tags: ['instagram', 'captions', 'social'],
    popular: true
  },
  {
    id: 'script-5',
    title: 'TikTok Script',
    prompt: 'Write a 60-second TikTok script about [TOPIC]. Start with a hook in the first 3 seconds, maintain fast pacing, include trending format elements, end with a strong CTA.',
    category: 'TikTok',
    tags: ['tiktok', 'short', 'viral']
  },
  {
    id: 'script-6',
    title: 'Email Marketing',
    prompt: 'Write a persuasive marketing email about [TOPIC/PRODUCT]. Include attention-grabbing subject line, compelling body copy, clear benefits, social proof, and strong call-to-action.',
    category: 'Marketing',
    tags: ['email', 'marketing', 'sales']
  },
  {
    id: 'script-7',
    title: 'Blog Post Outline',
    prompt: 'Create a detailed blog post outline about [TOPIC]. Include SEO-friendly headings, key points for each section, suggested word count, and internal linking opportunities.',
    category: 'Blogs',
    tags: ['blog', 'outline', 'seo']
  },
  {
    id: 'script-8',
    title: 'Podcast Episode',
    prompt: 'Create a podcast episode outline about [TOPIC]. Include intro script, 4-5 discussion points with talking notes, listener engagement questions, and outro with CTA.',
    category: 'Podcast',
    tags: ['podcast', 'outline', 'episode']
  }
];


interface PromptTemplatesProps {
  onSelectPrompt?: (prompt: string, type: 'image' | 'voice' | 'script') => void;
}

const PromptTemplates: React.FC<PromptTemplatesProps> = ({ onSelectPrompt }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('image');

  const filterTemplates = (templates: PromptTemplate[]) => {
    if (!searchQuery) return templates;
    const query = searchQuery.toLowerCase();
    return templates.filter(t => 
      t.title.toLowerCase().includes(query) ||
      t.prompt.toLowerCase().includes(query) ||
      t.tags.some(tag => tag.includes(query))
    );
  };

  const handleUseTemplate = (template: PromptTemplate, type: 'image' | 'voice' | 'script') => {
    if (onSelectPrompt) {
      onSelectPrompt(template.prompt, type);
      toast.success(`Template "${template.title}" applied!`);
    } else {
      navigator.clipboard.writeText(template.prompt);
      toast.success('Template copied to clipboard!');
    }
  };

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, React.ReactNode> = {
      'Portrait': <Camera className="w-3 h-3" />,
      'Fantasy': <Wand2 className="w-3 h-3" />,
      'Commercial': <Briefcase className="w-3 h-3" />,
      'Anime': <Sparkles className="w-3 h-3" />,
      'Food': <Heart className="w-3 h-3" />,
      'Nature': <Palette className="w-3 h-3" />,
      'Hooks': <Zap className="w-3 h-3" />,
      'Marketing': <TrendingUp className="w-3 h-3" />
    };
    return icons[category] || <Star className="w-3 h-3" />;
  };

  const renderTemplateCards = (templates: PromptTemplate[], type: 'image' | 'voice' | 'script') => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {filterTemplates(templates).map((template) => (
        <Card 
          key={template.id} 
          className="group bg-card/50 backdrop-blur-sm border-border/50 hover:shadow-lg hover:border-primary/30 transition-all duration-300 cursor-pointer"
          onClick={() => handleUseTemplate(template, type)}
        >
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <h4 className="font-semibold text-foreground">{template.title}</h4>
                {template.popular && (
                  <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    Popular
                  </Badge>
                )}
              </div>
              <Badge variant="outline" className="text-xs flex items-center gap-1">
                {getCategoryIcon(template.category)}
                {template.category}
              </Badge>
            </div>
            
            <p className="text-sm text-muted-foreground line-clamp-3">
              {template.prompt}
            </p>
            
            <div className="flex items-center justify-between pt-2">
              <div className="flex flex-wrap gap-1">
                {template.tags.slice(0, 3).map(tag => (
                  <Badge key={tag} variant="secondary" className="text-xs">
                    #{tag}
                  </Badge>
                ))}
              </div>
              
              <Button 
                size="sm" 
                variant="ghost"
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation();
                  handleUseTemplate(template, type);
                }}
              >
                <Copy className="w-4 h-4 mr-1" />
                Use
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  return (
    <Card className="border-0 shadow-xl rounded-3xl overflow-hidden bg-card">
      <CardHeader className="bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 p-6">
        <CardTitle className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="text-white flex-1">
            <h3 className="text-xl sm:text-2xl font-bold">Prompt Templates</h3>
            <p className="text-white/80 text-sm font-normal mt-1">
              Professional prompts to get started quickly
            </p>
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="p-6 space-y-6">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 border-2 border-border rounded-xl"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3 bg-muted/50 rounded-xl p-1">
            <TabsTrigger value="image" className="rounded-lg flex items-center gap-2">
              <Image className="w-4 h-4" />
              <span className="hidden sm:inline">Images</span>
            </TabsTrigger>
            <TabsTrigger value="voice" className="rounded-lg flex items-center gap-2">
              <Mic className="w-4 h-4" />
              <span className="hidden sm:inline">Voice</span>
            </TabsTrigger>
            <TabsTrigger value="script" className="rounded-lg flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Scripts</span>
            </TabsTrigger>
          </TabsList>

          <ScrollArea className="h-[500px] mt-4 pr-4">
            <TabsContent value="image" className="mt-0">
              {renderTemplateCards(imageTemplates, 'image')}
            </TabsContent>
            
            <TabsContent value="voice" className="mt-0">
              {renderTemplateCards(voiceTemplates, 'voice')}
            </TabsContent>
            
            <TabsContent value="script" className="mt-0">
              {renderTemplateCards(scriptTemplates, 'script')}
            </TabsContent>
          </ScrollArea>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default PromptTemplates;
